import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Activity,
    BookOpen,
    Coins,
    Crown,
    Scroll,
    Shield,
    Swords,
    Trophy,
    User,
    Check,
    X,
    Edit3,
    ShoppingBag,
    Search
} from 'lucide-react';
import { SupabaseService } from '../services/supabaseService';
import { CODEX_QUOTES, getProfileTitle } from '../utils/gameRules';
import { useAuth } from '../context/AuthContext';
import { TacticalFormation } from '../components/TacticalFormation';
import { QuickPlayModal } from '../components/QuickPlayModal';
import { useAlert } from '../context/AlertContext';
import { ProfileDossier } from '../components/ProfileDossier';
import { LeaderboardModal } from '../components/LeaderboardModal';
import { Profile } from '../types';

const Court: React.FC = () => {
    const navigate = useNavigate();
    const { error } = useAlert();
    const { user, loading, authReady } = useAuth();

    const [fullProfile, setFullProfile] = React.useState<Profile | null>(null);
    const [profile, setProfile] = React.useState({
        name: 'Commander',
        rank: 'Serf',
        gold: 500,
        tributeStatus: 'Pending',
        is_premium: false,
        rating: 1000,
        inventory: { p: 128, n: 24, b: 24, r: 32, q: 1, k: 1 }
    });

    const [activeWar, setActiveWar] = React.useState({
        exists: false,
        id: 'WAR-X',
        status: 'Peace',
    });

    const [leaderboard, setLeaderboard] = React.useState<{ name: string, score: number, rank: number }[]>([]);
    const [quoteIndex, setQuoteIndex] = React.useState(0);

    // Modals
    const [isUpgradeModalOpen, setIsUpgradeModalOpen] = React.useState(false);
    const [isNameModalOpen, setIsNameModalOpen] = React.useState(false);
    const [isQuickPlayModalOpen, setIsQuickPlayModalOpen] = React.useState(false);
    const [isProfileDossierOpen, setIsProfileDossierOpen] = React.useState(false);
    const [isLeaderboardOpen, setIsLeaderboardOpen] = React.useState(false);

    const [selectedPlan, setSelectedPlan] = React.useState<'monthly' | 'yearly' | null>(null);
    const [isSubmitting, setIsSubmitting] = React.useState(false);

    // Name Edit State
    const [newName, setNewName] = React.useState('');
    const [isFirstTime, setIsFirstTime] = React.useState(false);

    // Rotate Quotes
    React.useEffect(() => {
        const interval = setInterval(() => {
            setQuoteIndex((prev) => (prev + 1) % CODEX_QUOTES.length);
        }, 8000);
        return () => clearInterval(interval);
    }, []);

    const fetchProfileInfo = async () => {
        try {
            const p = await SupabaseService.getProfile();
            setFullProfile(p);
            const name = p.username.startsWith('Commander-') ? 'Commander' : p.username;
            setProfile({
                name,
                gold: p.gold,
                rank: getProfileTitle(p.rating),
                tributeStatus: p.is_premium ? 'Active' : 'Inactive',
                is_premium: p.is_premium,
                rating: p.rating,
                inventory: p.inventory
            });

            // Check for default username (Onboarding)
            const defaultName = user?.email?.split('@')[0];
            if (p.username === defaultName || p.username === 'Commander' || p.username === 'Guest Commander' || p.username.startsWith('Commander-')) {
                setIsFirstTime(true);
                setIsNameModalOpen(true);
            }
        } catch (err) {
            console.error("Court: Failed to fetch profile info:", err);
        }
    };

    const fetchLeaderboard = async () => {
        try {
            const leaders = await SupabaseService.getLeaderboard();
            setLeaderboard(leaders.map((l, index) => ({
                name: l.username,
                score: l.rating,
                rank: index + 1
            })));
        } catch (err) {
            console.error("Court: Failed to fetch leaderboard:", err);
            setLeaderboard([]);
        }
    };

    const fetchActiveWar = async () => {
        try {
            const war = await SupabaseService.getActiveWar();
            if (war) {
                setActiveWar({ exists: true, status: 'Active Conflict', id: war.id });
            } else {
                setActiveWar({ exists: false, status: 'Operational', id: 'NONE' });
            }
        } catch (err) {
            console.error("Court: Failed to fetch active war:", err);
            setActiveWar({ exists: false, status: 'Operational', id: 'NONE' });
        }
    };

    const loadProfile = async () => {
        if (!authReady || !user?.id) return;
        await fetchProfileInfo();
        await fetchLeaderboard();
        await fetchActiveWar();
    };

    React.useEffect(() => {
        if (!authReady || !user?.id) return;
        loadProfile();
    }, [authReady, user?.id]);

    if (!authReady || loading) {
        return (
            <div className="min-h-screen bg-tudor-black flex items-center justify-center text-tudor-gold">
                <div className="flex flex-col items-center gap-4 animate-fade-in">
                    <Crown className="w-12 h-12 animate-pulse opacity-50" />
                    <span className="text-xs uppercase tracking-[0.3em] font-mono">Restoring Court Session...</span>
                </div>
            </div>
        );
    }

    const handleSubscribe = async () => {
        if (!selectedPlan) return;
        setIsSubmitting(true);
        try {
            await SupabaseService.subscribeToPremium(selectedPlan);
        } catch (err: any) {
            console.error("Subscription error:", err);
            error(`Failed to initiate tribute protocol: ${err.message || 'Unknown error. Ensure Edge Function is deployed.'}`);
            setIsSubmitting(false);
        }
    };

    const handleUpdateName = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!newName.trim()) return;

        try {
            await SupabaseService.updateProfile({ username: newName });

            // Re-fetch the full profile to properly reflect changes in all UI
            const updated = await SupabaseService.getProfile();
            setFullProfile(updated);

            setProfile(p => ({ ...p, name: newName }));
            setIsNameModalOpen(false);
        } catch (err: any) {
            console.error("Failed to update name", err);
            if (err.message === "Not authenticated") {
                // If not authenticated (e.g. playing offline or mock user), allow the local update
                if (fullProfile) {
                    setFullProfile({ ...fullProfile, username: newName });
                }
                setProfile(p => ({ ...p, name: newName }));
                setIsNameModalOpen(false);
            } else {
                error(err.message || "Callsign already taken. Choose another.");
            }
        }
    };

    const navItems = [
        {
            title: 'Royal Archives',
            description: 'Campaign dossiers and war fronts.',
            route: '/campaigns',
            icon: Scroll,
            accent: 'text-tudor-gold',
            meta: 'Campaigns',
            onClick: () => navigate('/campaigns')
        },
        {
            title: 'Valor Battles',
            description: 'Instant skirmishes.',
            route: '#',
            icon: Swords,
            accent: 'text-tudor-gold',
            meta: 'Matchmaking',
            onClick: () => setIsQuickPlayModalOpen(true)
        },
        {
            title: 'Barracks',
            description: 'Recruit and manage your standing army.',
            route: '/barracks',
            icon: ShoppingBag,
            accent: 'text-tudor-gold',
            meta: 'Recruitment',
            onClick: () => navigate('/barracks')
        },
        {
            title: 'Royal Treasury',
            description: 'Account, tributes, and titles.',
            route: '/account',
            icon: Coins,
            accent: 'text-amber-400',
            meta: 'Holdings',
            onClick: () => {
                if (!profile.is_premium) {
                    setIsUpgradeModalOpen(true);
                } else {
                    navigate('/account');
                }
            }
        },
    ];

    return (
        <div className="min-h-[calc(100vh-6rem)] w-full bg-black px-4 pb-4 text-zinc-200 font-mono">
            {/* MODALS */}
            {fullProfile && (
                <ProfileDossier
                    isOpen={isProfileDossierOpen}
                    onClose={() => setIsProfileDossierOpen(false)}
                    profile={fullProfile}
                    onUpgrade={() => {
                        setIsProfileDossierOpen(false);
                        setIsUpgradeModalOpen(true);
                    }}
                />
            )}

            <LeaderboardModal
                isOpen={isLeaderboardOpen}
                onClose={() => setIsLeaderboardOpen(false)}
                currentUserId={user?.id}
            />

            <QuickPlayModal isOpen={isQuickPlayModalOpen} onClose={() => setIsQuickPlayModalOpen(false)} />

            {/* UPGRADE MODAL */}
            {isUpgradeModalOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/45 backdrop-blur-sm p-4 animate-fade-in">
                    <div className="w-full max-w-2xl bg-zinc-900 border-2 border-tudor-gold/30 p-8 rounded-lg relative overflow-hidden shadow-2xl">
                        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-tudor-gold/5 via-transparent to-transparent opacity-50 pointer-events-none"></div>
                        <button
                            onClick={() => { setIsUpgradeModalOpen(false); setSelectedPlan(null); }}
                            className="absolute top-4 right-4 text-zinc-500 hover:text-white"
                        >
                            <X size={24} />
                        </button>
                        <div className="text-center mb-8">
                            <Crown className="w-12 h-12 text-tudor-gold mx-auto mb-4" />
                            <h2 className="text-3xl font-serif text-tudor-gold tracking-widest uppercase">Royal Tribute</h2>
                            <p className="text-gray-400 mt-2 text-sm uppercase tracking-wider">Select your contribution level</p>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                            <button
                                onClick={() => setSelectedPlan('monthly')}
                                className={`group relative border p-6 transition-all text-left ${selectedPlan === 'monthly' ? 'border-tudor-gold bg-tudor-gold/20' : 'border-zinc-700 bg-black/50 hover:border-zinc-500'}`}
                            >
                                <div className="text-xs text-zinc-500 uppercase tracking-widest mb-2">Monthly Tribute</div>
                                <div className="text-2xl font-mono text-white mb-4">€3.99 <span className="text-sm text-zinc-600">/ mo</span></div>
                                <ul className="text-xs text-gray-400 space-y-2">
                                    <li className="flex items-center gap-2"><Check size={12} className="text-tudor-gold" /> Command Multiple Warfronts</li>
                                    <li className="flex items-center gap-2"><Check size={12} className="text-tudor-gold" /> Lead several battles at once</li>
                                    <li className="flex items-center gap-2"><Check size={12} className="text-tudor-gold" /> Unlimited Ranked & Casual Campaigns</li>
                                </ul>
                                {selectedPlan === 'monthly' && (
                                    <div className="absolute top-2 right-2 text-tudor-gold"><Check size={16} /></div>
                                )}
                            </button>
                            <button
                                onClick={() => setSelectedPlan('yearly')}
                                className={`group relative border p-6 transition-all text-left ${selectedPlan === 'yearly' ? 'border-tudor-gold bg-tudor-gold/20' : 'border-tudor-gold/50 bg-tudor-gold/5 hover:bg-tudor-gold/10'}`}
                            >
                                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-tudor-gold text-black text-[10px] font-bold px-2 py-0.5 uppercase tracking-wider">Best Value</div>
                                <div className="text-xs text-tudor-gold uppercase tracking-widest mb-2">Annual Tribute</div>
                                <div className="text-2xl font-mono text-white mb-4">€39.99 <span className="text-sm text-zinc-600">/ yr</span></div>
                                <ul className="text-xs text-gray-400 space-y-2">
                                    <li className="flex items-center gap-2"><Check size={12} className="text-tudor-gold" /> 2 Months Free</li>
                                    <li className="flex items-center gap-2"><Check size={12} className="text-tudor-gold" /> Command Multiple Warfronts</li>
                                    <li className="flex items-center gap-2"><Check size={12} className="text-tudor-gold" /> Lead several battles at once</li>
                                    <li className="flex items-center gap-2"><Check size={12} className="text-tudor-gold" /> Unlimited Ranked & Casual Campaigns</li>
                                </ul>
                                {selectedPlan === 'yearly' && (
                                    <div className="absolute top-2 right-2 text-tudor-gold"><Check size={16} /></div>
                                )}
                            </button>
                        </div>
                        <div className="flex justify-center">
                            <button
                                onClick={handleSubscribe}
                                disabled={!selectedPlan || isSubmitting}
                                className={`w-full max-w-sm py-3 px-6 rounded-full uppercase tracking-[0.25em] font-bold text-sm transition-all ${!selectedPlan ? 'bg-zinc-800 text-zinc-600 cursor-not-allowed' : 'bg-tudor-gold text-black hover:bg-white hover:scale-105 active:scale-95 shadow-[0_0_20px_rgba(180,142,61,0.3)]'} ${isSubmitting ? 'opacity-80 cursor-wait' : ''}`}
                            >
                                {isSubmitting ? 'Initiating Protocol...' : selectedPlan ? `Confirm Tribute` : 'Select a Plan'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* NAME MODAL */}
            {isNameModalOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/95 backdrop-blur-md p-4 animate-fade-in overflow-y-auto">
                    <div className="w-full max-w-md bg-zinc-900 border border-tudor-gold/30 p-8 rounded-sm relative shadow-2xl my-8">
                        <button onClick={() => setIsNameModalOpen(false)} className="absolute top-4 right-4 text-zinc-500 hover:text-white"><X size={20} /></button>
                        <div className="text-center mb-6">
                            <User className="w-12 h-12 text-tudor-gold mx-auto mb-4 animate-pulse" />
                            <h2 className="text-2xl font-serif text-tudor-gold tracking-widest uppercase">Identity Protocol</h2>
                            <p className="text-gray-400 mt-2 text-xs uppercase tracking-wider">Establish your callsign on the battlefield.</p>
                        </div>
                        <form onSubmit={handleUpdateName} className="space-y-6">
                            <div>
                                <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-2">Desired Callsign</label>
                                <input
                                    type="text"
                                    value={newName}
                                    onChange={(e) => setNewName(e.target.value)}
                                    className="w-full bg-black border border-zinc-700 p-4 text-center text-xl text-tudor-gold font-serif tracking-widest focus:border-tudor-gold focus:outline-none placeholder-zinc-800"
                                    placeholder="ENTER NAME"
                                    maxLength={20}
                                    autoFocus
                                />
                            </div>
                            <button className="w-full bg-tudor-gold text-black font-bold py-4 uppercase tracking-[0.2em] hover:bg-white transition-colors">Confirm Identity</button>
                        </form>

                        {isFirstTime && (
                            <div className="mt-8 pt-6 border-t border-zinc-800">
                                <h3 className="text-tudor-gold font-serif tracking-widest uppercase mb-4 text-center text-lg flex items-center justify-center gap-2">
                                    <BookOpen size={18} /> How to Play
                                </h3>
                                <div className="space-y-4 text-sm text-zinc-400">
                                    <div className="flex gap-3">
                                        <div className="text-tudor-gold pt-1"><Shield size={16} /></div>
                                        <div>
                                            <p className="text-white font-bold uppercase tracking-wider text-xs mb-1">Permanent Casualties</p>
                                            <p className="text-xs">Unlike traditional chess, pieces lost in battle are permanently removed from your reserve inventory.</p>
                                        </div>
                                    </div>
                                    <div className="flex gap-3">
                                        <div className="text-tudor-gold pt-1"><Coins size={16} /></div>
                                        <div>
                                            <p className="text-white font-bold uppercase tracking-wider text-xs mb-1">The Draft</p>
                                            <p className="text-xs">Use starting gold to recruit your army before the campaign begins. Spend wisely to construct a balanced force.</p>
                                        </div>
                                    </div>
                                    <div className="flex gap-3">
                                        <div className="text-tudor-gold pt-1"><Activity size={16} /></div>
                                        <div>
                                            <p className="text-white font-bold uppercase tracking-wider text-xs mb-1">Tactical Surrender</p>
                                            <p className="text-xs">You can surrender a lost battle at any time to save your surviving units for the next engagement.</p>
                                        </div>
                                    </div>
                                </div>

                                <button
                                    onClick={() => navigate('/rules')}
                                    className="w-full mt-6 py-3 border border-zinc-700 hover:border-tudor-gold text-zinc-400 hover:text-tudor-gold text-xs uppercase tracking-widest transition-colors flex items-center justify-center gap-2"
                                >
                                    <Scroll size={14} /> Read Full Rulebook
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}

            <div className="relative overflow-hidden rounded-3xl border border-zinc-800 bg-zinc-950 px-5 py-5">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(180,142,61,0.18),_transparent_55%)]"></div>
                <div className="absolute inset-0 bg-[linear-gradient(120deg,transparent_35%,rgba(255,255,255,0.04)_50%,transparent_65%)] opacity-40"></div>

                <div className="relative z-10 flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
                    <div>
                        <div className="flex items-center gap-3 text-xs uppercase tracking-[0.35em] text-zinc-500">
                            <span className="h-1 w-8 bg-tudor-gold/40"></span>
                            <h1 className="text-2xl md:text-4xl font-serif text-tudor-gold tracking-[0.18em]">Welcome</h1>
                            <span className="h-1 w-8 bg-tudor-gold/40"></span>
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-3">
                        <div className="flex items-center gap-2 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-4 py-2 text-xs uppercase tracking-widest text-emerald-400">
                            <Activity size={14} />
                            {activeWar.status}
                        </div>
                        {profile.is_premium ? (
                            <div className="flex items-center gap-2 rounded-full border border-tudor-gold/40 bg-tudor-gold/10 px-4 py-2 text-xs uppercase tracking-widest text-tudor-gold">
                                <Trophy size={14} /> Tribute Active
                            </div>
                        ) : (
                            <button
                                onClick={() => setIsUpgradeModalOpen(true)}
                                className="flex items-center gap-2 rounded-full border border-zinc-500/40 bg-zinc-500/10 px-4 py-2 text-xs uppercase tracking-widest text-zinc-400 hover:border-tudor-gold hover:text-tudor-gold transition-colors"
                            >
                                <Trophy size={14} /> Silver (Free)
                            </button>
                        )}
                    </div>
                </div>

                <div className="relative z-10 mt-4 grid gap-3 lg:grid-cols-[2fr,1fr]">
                    {/* THRONE DIRECTIVE */}
                    <div className="group relative overflow-hidden rounded-2xl border border-zinc-800 bg-gradient-to-br from-zinc-950 via-zinc-900 to-black p-4 transition-all md:hover:border-tudor-gold/50 active:scale-[0.99]">
                        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/dark-mosaic.png')] opacity-10"></div>
                        <div className="relative z-10 flex h-full flex-col justify-between gap-6">
                            <div>
                                <div className="flex items-center gap-3 text-sm uppercase tracking-[0.3em] text-zinc-500">
                                    <Swords size={18} className="text-zinc-500" /> Throne Directive
                                </div>
                                <h2 className="mt-2 text-xl md:text-3xl font-serif text-zinc-200 transition-colors md:group-hover:text-tudor-gold">
                                    {activeWar.exists ? 'Resume the Crowned Conflict' : 'Commission a New War'}
                                </h2>
                                <p className="mt-2 text-xs md:text-sm text-zinc-400">
                                    {activeWar.exists
                                        ? `Consolidate forces and return to the war room.`
                                        : 'No active conflict detected. The continent awaits to crown the next King.'}
                                </p>
                            </div>
                            <div className="flex flex-wrap items-center gap-4">
                                <button
                                    type="button"
                                    onClick={() => navigate(activeWar.exists ? `/war-room/${activeWar.id}` : '/drafting')}
                                    className="group inline-flex items-center gap-3 rounded-full border border-tudor-gold bg-tudor-gold/10 px-6 py-3 text-xs uppercase tracking-[0.35em] text-tudor-gold transition-all md:hover:bg-tudor-gold md:hover:text-black active:bg-tudor-gold active:text-black"
                                >
                                    {activeWar.exists ? 'Return to War Room' : 'Start Campaign'}
                                    <span className="text-lg transition-transform group-hover:translate-x-1">→</span>
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* HIGH COUNCIL (Leaderboard Preview) */}
                    <div className="group rounded-2xl border border-zinc-800 bg-zinc-950/80 backdrop-blur-sm p-4 transition-all md:hover:border-tudor-gold/50 flex flex-col">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-sm uppercase tracking-[0.2em] text-zinc-500 flex items-center gap-2">
                                <Crown size={14} className="text-tudor-gold" /> High Council
                            </h3>
                            <button
                                onClick={() => setIsLeaderboardOpen(true)}
                                className="text-[10px] text-tudor-gold hover:text-white uppercase tracking-wider border border-tudor-gold/30 px-2 py-1 rounded hover:bg-tudor-gold/10 transition-colors"
                            >
                                View All
                            </button>
                        </div>

                        <div className="space-y-2 flex-1">
                            {leaderboard.slice(0, 3).map((l, i) => (
                                <div key={i} className="flex items-center justify-between text-xs p-2 bg-black/40 rounded border border-zinc-900">
                                    <div className="flex items-center gap-3">
                                        <span className={`text-zinc-600 font-mono w-4 text-center ${i === 0 ? 'text-tudor-gold' : ''}`}>#{l.rank}</span>
                                        <span className="text-zinc-300">{l.name}</span>
                                    </div>
                                    <span className="font-mono text-zinc-500">{l.score}</span>
                                </div>
                            ))}
                        </div>

                        <div className="mt-4 pt-4 border-t border-zinc-900 group-hover:border-zinc-800 transition-colors">
                            <div className="flex items-center justify-between">
                                <div>
                                    <div className="mt-1 text-xl font-serif text-zinc-200 transition-colors md:group-hover:text-tudor-gold flex items-center gap-2">
                                        {profile.name}
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                if (!user) {
                                                    navigate('/auth');
                                                    return;
                                                }
                                                setIsNameModalOpen(true);
                                            }}
                                            className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:text-tudor-gold text-zinc-600"
                                            title="Rename"
                                        >
                                            <Edit3 size={14} />
                                        </button>
                                    </div>
                                    <div className="text-sm text-zinc-400">{profile.rank}</div>
                                </div>
                                <div className="rounded-full border border-zinc-700 bg-black/40 p-3 group-hover:border-tudor-gold/50 cursor-pointer" onClick={() => setIsProfileDossierOpen(true)}>
                                    <User className="text-tudor-gold" />
                                </div>
                            </div>
                            <div className="mt-4 flex gap-4 text-xs uppercase tracking-[0.3em] text-zinc-500">
                                <div className="flex items-center gap-2">
                                    <Shield size={12} />
                                    <span className="text-zinc-300 font-mono font-bold tracking-[0.2em]">{profile.rating}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Coins size={12} />
                                    <span className="text-tudor-gold font-bold tracking-[0.2em]">{profile.gold}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="mt-3 grid grid-cols-2 gap-3 lg:grid-cols-4">
                    {navItems.map((item) => (
                        <button
                            key={item.title}
                            onClick={item.onClick}
                            className="group relative overflow-hidden rounded-2xl border border-zinc-800 bg-zinc-950 p-4 text-left transition-all hover:border-tudor-gold hover:-translate-y-1"
                        >
                            <div className="flex items-center justify-between mb-3">
                                <div className={`rounded-xl bg-zinc-900/50 p-2.5 ${item.accent} transition-colors group-hover:bg-tudor-gold group-hover:text-black`}>
                                    <item.icon size={20} />
                                </div>
                                <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-600 group-hover:text-tudor-gold/70">{item.meta}</span>
                            </div>
                            <h3 className="font-serif text-lg text-zinc-200 group-hover:text-tudor-gold">{item.title}</h3>
                            <p className="mt-1 text-xs text-zinc-500 group-hover:text-zinc-400">{item.description}</p>
                        </button>
                    ))}
                </div>

                <div className="mt-6 flex flex-col items-center justify-center gap-2 text-center">
                    <div className="h-px w-24 bg-gradient-to-r from-transparent via-zinc-800 to-transparent"></div>
                    <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-zinc-600">
                        <BookOpen size={12} />
                        <span>Codex Imperialis &bull; Vol. {quoteIndex + 1}</span>
                    </div>
                    <p className="max-w-xl font-serif text-sm italic text-zinc-500 transition-opacity duration-500 selection:bg-tudor-gold selection:text-black">
                        "{CODEX_QUOTES[quoteIndex]}"
                    </p>
                </div>
            </div>
        </div>
    );
};

export default Court;
